// How far down the Book & pay screen (/booking/book) a visitor got, and what
// they had filled in when they left. Robin, 9 Oct 2026: "what we need to
// track is progress through the booking form from top to bottom..". The
// events are in ANALYTICS.md ("Progress through Book & pay"); the DOM side
// (IntersectionObserver, page lifecycle) is
// pages/booking/components/step-book/use-booking-progress.js. This file has
// no DOM and no React, so node --test can drive it
// (booking-progress.test.mjs).

// The screen's sections, top to bottom, as a phone shows them. Each one is a
// `data-book-section` wrapper in step-book. `details` is the postcode, house
// number, note and mobile, stacked above the pay buttons (since the trim of
// 9 Oct 2026, which moved the note there and merged 'address' and 'contact').
// section_index is the 1-based position here.
export const BOOK_SECTIONS = ['wash', 'car_size', 'date', 'time_slot', 'extras', 'details', 'pay_buttons']

// The fields a booking needs, in the order the screen asks for them.
// `house_number` is the form's `address` field (its analytics key since
// 5 Oct 2026, see step-book's ANALYTICS_FIELD).
// `car_location` is the one Car location box (19 Oct 2026). Since 25 Oct
// 2026 it is the only location box, with the lookup off or "Enter it
// manually" too, so step-book reports `postcode` and `house_number` no
// more; the keys stay so older events and the PostHog config still read.
export const BOOK_FIELDS = ['car_size', 'date', 'time_slot', 'car_location', 'postcode', 'house_number', 'mobile']

// FORM V3 (20 Oct 2026, docs/DECISIONS.md; lib/form-version.js): wash, your
// car (the Registration box, the size the plate sets, or the size buttons),
// car location, day, time, extras (folded), the details (note, mobile), pay.
// So section_index moved: 'car' (2) replaces 'car_size', 'location' (3) is
// new, and date to pay_buttons are one further down (4 to 8). With the
// wallet sheet's address on, 'location' is in the card section, below.
export const BOOK_SECTIONS_V3 = ['wash', 'car', 'location', 'date', 'time_slot', 'extras', 'details', 'pay_buttons']
export const BOOK_FIELDS_V3 = ['car_size', 'car_location', 'postcode', 'house_number', 'date', 'time_slot', 'mobile']

export const sectionIndex = (name, sections = BOOK_SECTIONS) => sections.indexOf(name) + 1

// "At least half of it is on screen". A section taller than twice the
// viewport (the open wash picker on a small phone) can never be 50% visible,
// so one that fills half the viewport counts too.
export const halfVisible = (entry, viewportHeight) => {
  if (!entry || !entry.isIntersecting) return false
  if (entry.intersectionRatio >= 0.5) return true
  const rootHeight = (entry.rootBounds && entry.rootBounds.height) || viewportHeight || 0
  const shown = (entry.intersectionRect && entry.intersectionRect.height) || 0
  return rootHeight > 0 && shown >= rootHeight / 2
}

const isFilled = value => {
  if (value instanceof Date) return !Number.isNaN(value.getTime())
  if (value === null || value === undefined || value === false) return false
  return String(value).trim() !== ''
}

// values: { car_size, date, time_slot, postcode, house_number, mobile }
// -> the ones with a value, in screen order.
export const filledFields = (values, list = BOOK_FIELDS) => list.filter(name => isFilled(values && values[name]))

// The note's words and whether the house number box holds anything, for
// booking_form_exit, pay_panel_shown and the pay press that goes through
// (booking_step_completed, step 'book'). Robin, 17 Oct 2026: "we should
// write the notes to posthog as well tbh.. nothing is very private there.."
// (docs/DECISIONS.md). notes_text is the trimmed note, cut at
// NOTES_TEXT_MAX characters, null when empty; notes_length is the trimmed
// note's full length. The house number's text is never sent, only
// house_number_filled (it shows in replays instead).
export const NOTES_TEXT_MAX = 500
// second_car_mentioned: the note answers its "second car" prompt (20 Oct
// 2026). Same words as PostHog::OrderEventWorker::SECOND_CAR on order_paid.
export const SECOND_CAR = /\b(second|another) car|\b(2|two) cars/i
export const noteProps = values => {
  const raw = values && values.notes
  const note = raw === null || raw === undefined ? '' : String(raw).trim()
  return {
    notes_text: note ? note.slice(0, NOTES_TEXT_MAX) : null,
    notes_length: note.length,
    second_car_mentioned: SECOND_CAR.test(note),
    // Since 25 Oct 2026 the house is typed in the one Car location box.
    house_number_filled: isFilled(values && values.house_number) || isFilled(values && values.car_location)
  }
}

// The boxes typed into (the rest are taps), and the ones that make up the
// details block above the pay buttons (`details_engaged`).
const TEXT_FIELDS = ['car_location', 'postcode', 'house_number', 'mobile', 'notes']
const DETAILS_FIELDS = ['car_location', 'postcode', 'house_number', 'notes', 'mobile']

// Where a value came from (fill_source), since 11 Oct 2026
// (address-leavers-2026-10-11.md §6):
//   typed      the visitor typed it, pasted it, or tapped it (size, day, slot)
//   autofill   the browser or a password manager put it in: a jump of more
//              than 3 characters in one input event, or a change to a box
//              that does not have the focus
//   prefill    the app put it there: the signed-in customer's saved mobile,
//              the default "Anytime" slot
//   persisted  the saved draft put it back (a reload, a return from Stripe)
// A user source (typed, autofill) replaces an app one, never the other way.
export const FILL_SOURCES = ['typed', 'autofill', 'prefill', 'persisted']
const APP_SOURCES = ['prefill', 'persisted']

// One input event in a text box: typed (a few characters, a paste or a
// deletion, with the box focused) or autofill (anything else).
const USER_INPUT = /^(insertFromPaste|delete)/
export const inputSource = ({ focused, delta, inputType } = {}) => (
  focused && (USER_INPUT.test(inputType || '') || Math.abs(Number(delta) || 0) <= 3) ? 'typed' : 'autofill'
)

// One per page view of the booking screen.
//   sectionSeen(name)  a section came into view; true the first time only,
//                      which is when onSection({ section, section_index }) runs
//   notePay(ok)        a pay button was pressed; ok = the press went through
//                      to payment (a refused one counts in refused_presses)
//   noteStickyBar()    the phone's sticky Book bar was tapped
//   noteScroll(pct)    how far down the page the bottom of the screen is
//   noteEngaged(field) the visitor tapped or focused a field (BOOK_FIELDS or
//                      notes) on this page view; a tap field (size, day,
//                      slot) is typed from then on
//   noteInput(field, { focused, delta, inputType })
//                      an input event in a text box (inputSource above)
//   noteSource(field, source)
//                      the app set a value: 'prefill' or 'persisted'
//   resume()           the page came back from the back-forward cache
//   reopen()           the tab is visible again after a 'hidden' (or
//                      'pagehide') exit: the page view goes on, and the next
//                      exit is sent too, with exit_seq one higher
//   exit(trigger, { fieldValues, viewportHeight })
//                      the visitor left: onExit(props) once per page view
//                      (once per reopen). trigger: 'hidden' | 'pagehide' |
//                      'route' | 'completed'.
//                      'completed' (the payment landed on the success page)
//                      sends nothing and ends the page view. Once a press has
//                      gone through to payment, 'hidden' and 'pagehide' are
//                      the Stripe redirect or the wallet sheet, so they send
//                      nothing either; a later route change away still does.
// sections, fields: the layout's lists (BOOK_SECTIONS_V3 and BOOK_FIELDS_V3 on
// Form v3).
export const createBookingProgress = ({ onSection = () => {}, onExit = () => {}, now = () => Date.now(), sections = BOOK_SECTIONS, fields = BOOK_FIELDS } = {}) => {
  const startedAt = now()
  const seen = new Set()
  let furthest = 0
  let payPressed = false
  let refusedPresses = 0
  let payStarted = false
  let stickyBarTapped = false
  let maxScrollPct = 0
  let done = false
  let lastTrigger = null
  let exitSeq = 0
  const engaged = new Set()
  const sources = {}

  return {
    sectionSeen (name) {
      const index = sectionIndex(name, sections)
      if (done || !index || seen.has(name)) return false
      seen.add(name)
      if (index > furthest) furthest = index
      onSection({ section: name, section_index: index })
      return true
    },
    notePay (ok) {
      payPressed = true
      if (ok) payStarted = true
      else refusedPresses += 1
    },
    noteStickyBar () { stickyBarTapped = true },
    noteScroll (pct) {
      const value = Math.max(0, Math.min(100, Math.round(Number(pct) || 0)))
      if (value > maxScrollPct) maxScrollPct = value
    },
    noteEngaged (field) {
      if (!field) return
      engaged.add(field)
      if (!TEXT_FIELDS.includes(field)) sources[field] = 'typed'
    },
    noteInput (field, event) {
      if (!field) return
      sources[field] = inputSource(event)
      if (event && event.focused) engaged.add(field)
    },
    noteSource (field, source) {
      if (!field || !APP_SOURCES.includes(source)) return
      if (!sources[field] || APP_SOURCES.includes(sources[field])) sources[field] = source
    },
    resume () { payStarted = false },
    reopen () {
      if (!done || (lastTrigger !== 'hidden' && lastTrigger !== 'pagehide')) return false
      done = false
      lastTrigger = null
      return true
    },
    get sent () { return done },
    exit (trigger, { fieldValues, viewportHeight } = {}) {
      if (done) return null
      if (trigger === 'completed') { done = true; lastTrigger = trigger; return null }
      if (payStarted && trigger !== 'route') return null
      done = true
      lastTrigger = trigger
      exitSeq += 1
      const withValue = filledFields(fieldValues, fields)
      // A field counts as filled only when the visitor engaged it on this
      // page view: not the default slot, not a saved mobile, not a value the
      // draft put back or the browser filled into a box nobody touched.
      const filled = withValue.filter(name => engaged.has(name))
      const sourceOf = name => sources[name] || (TEXT_FIELDS.includes(name) ? 'autofill' : 'prefill')
      const fillSource = {}
      withValue.forEach(name => { fillSource[name] = sourceOf(name) })
      if (isFilled(fieldValues && fieldValues.notes)) fillSource.notes = sourceOf('notes')
      const props = {
        exit_trigger: trigger,
        exit_seq: exitSeq,
        furthest_section_seen: furthest ? sections[furthest - 1] : null,
        furthest_section_index: furthest,
        sections_seen: sections.filter(name => seen.has(name)),
        furthest_field_filled: filled.length ? filled[filled.length - 1] : null,
        fields_filled: filled,
        fields_typed: withValue.filter(name => sourceOf(name) === 'typed'),
        fill_source: fillSource,
        details_engaged: DETAILS_FIELDS.some(name => engaged.has(name)),
        pay_pressed: payPressed,
        refused_presses: refusedPresses,
        seconds_on_page: Math.max(0, Math.round((now() - startedAt) / 1000)),
        max_scroll_pct: maxScrollPct,
        viewport_height: Math.round(Number(viewportHeight) || 0),
        sticky_bar_tapped: stickyBarTapped,
        notes_entered: isFilled(fieldValues && fieldValues.notes),
        ...noteProps(fieldValues)
      }
      onExit(props)
      return props
    }
  }
}
